// Trying an option of the colours experiment (ColorsExperiment.tsx) on the whole app: its tokens and rules set on
// <html>, kept in the browser, so it stays while the app is clicked through and after a reload, until it's reset there.
// Only the experiment page sets it; the app restores it on start (index.tsx).

export type Tokens = Record<string, string>;

// Where the accent goes, each on top of the one before it except `rail` and `secondary`, which add to orientation;
// `chrome` gives the rail and the top bar a surface of their own, `rail-chrome` only the rail; `quiet-nav` marks the
// current page in the rail with the accent's icon on a light gray rather than on the accent's tint; `dark-rail` makes
// the rail dark on a light page. The layouts of round 8: `rail-surface` and `top-surface` give the rail and the top bar
// surfaces of their own (`rail`, `top`), `rail-dark` and `top-dark` make them dark (`chrome-dark`), `inset` sets the page
// into the rail's surface as a panel.
export type Usage =
    | 'actions'
    | 'orientation'
    | 'surfaces'
    | 'rail'
    | 'secondary'
    | 'chrome'
    | 'rail-chrome'
    | 'quiet-nav'
    | 'dark-rail'
    | 'rail-surface'
    | 'top-surface'
    | 'rail-dark'
    | 'top-dark'
    | 'inset';

// A whole visual language's rules beyond its colours (SCOPE_CSS, `data-language`).
export type Language = 'paper' | 'workshop' | 'atelier' | 'editorial' | 'workbench' | 'office' | 'evening' | 'letter';

// The font of the titles, section titles and big numbers.
export interface Headline {
    family: string;
    weight: number;
    tracking: string;
}

export interface Preview {
    // What the experiment page shows as applied, e.g. "3C Atelier".
    id: string;
    light: Tokens;
    dark: Tokens;
    usage: Usage[];
    language?: Language;
    isWarm?: boolean;
    headline?: Headline;
    // What the playground was set to, so it starts from there again.
    playground?: { paletteId: string; layoutLabel: string; fontLabel: string };
}

// Every font the options try, from Google Fonts, as the experiment is only looked at; a picked one is installed.
const FONTS_URL =
    'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400..800&family=Fraunces:ital,opsz,wght@0,9..144,300..800;1,9..144,300..800&family=Geist:wght@400..700&family=Instrument+Sans:wght@400..700&family=Instrument+Serif:ital@0;1&family=JetBrains+Mono:wght@400..600&family=Newsreader:ital,opsz,wght@0,6..72,300..700;1,6..72,300..700&family=Schibsted+Grotesk:wght@400..800&family=Young+Serif&family=Hanken+Grotesk:wght@400..800&family=Plus+Jakarta+Sans:wght@400..800&family=Source+Serif+4:opsz,wght@8..60,400..700&family=Literata:opsz,wght@7..72,400..700&family=IBM+Plex+Serif:wght@400;500;600;700&family=Figtree:wght@400..800&family=Manrope:wght@400..800&family=DM+Serif+Display&display=swap';

const FONTS_ID = 'colors-experiment-fonts';
const STYLE_ID = 'colors-experiment-preview';
const STORAGE_KEY = 'colors-experiment-preview';

export const loadFonts = () => {
    if (document.getElementById(FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = FONTS_ID;
    link.rel = 'stylesheet';
    link.href = FONTS_URL;
    document.head.append(link);
};

export const headlineVariables = (headline?: Headline): Record<string, string> =>
    headline
        ? {
              '--headline-font': headline.family,
              '--headline-weight': String(headline.weight),
              '--headline-tracking': headline.tracking,
          }
        : {};

// Rules for what's inside a scope (a frame, or <html> for the whole app) that stop at a frame inside it, so an option
// put on the whole app doesn't reach into the frames on the experiment page.
const inScope = (root: string, body: string) => `@scope (${root}) to (:scope .palette-scope) {\n${body}\n}\n`;

// What hands the accent to the parts that take the text colour today, and what each visual language changes. Selectors
// on the components' classes, which is fine for a look at the options; a picked one gets tokens and props instead.
// A dark rail or top bar: its own surface (`chrome-dark`) with light text on it, and the accent lightened for it.
const CHROME_DARK = `
    --color-surface: var(--color-chrome-dark);
    --color-surface-muted: color-mix(in srgb, #ffffff 7%, var(--color-chrome-dark));
    --color-surface-hover: color-mix(in srgb, #ffffff 11%, var(--color-chrome-dark));
    --color-border: color-mix(in srgb, #ffffff 9%, var(--color-chrome-dark));
    --color-text: #f4f2ee;
    --color-text-muted: color-mix(in srgb, #ffffff 60%, var(--color-chrome-dark));
    --color-text-subtle: color-mix(in srgb, #ffffff 35%, var(--color-chrome-dark));
    --color-accent-surface: color-mix(in srgb, var(--color-accent-on-dark) 22%, var(--color-chrome-dark));
    --color-accent-text: var(--color-accent-on-dark);
    --color-nav-active: var(--color-accent-surface);
    --color-nav-active-text: var(--color-accent-text);
    background-color: var(--color-surface);
    color: var(--color-text);
`;

export const SCOPE_CSS =
    `
.palette-scope {
    background-color: var(--color-surface);
    color: var(--color-text);
    --color-surface-shaded: color-mix(in srgb, var(--color-surface-muted) 60%, var(--color-surface));
    --color-surface-faint: color-mix(in srgb, var(--color-surface-shaded) 50%, var(--color-surface));
    --radius-card: 1rem;
}
.palette-scope[data-warm] {
    --color-positive-surface: color-mix(in srgb, var(--color-positive) 13%, var(--color-surface));
    --color-negative-surface: color-mix(in srgb, var(--color-negative) 12%, var(--color-surface));
    --color-caution-surface: color-mix(in srgb, var(--color-caution) 13%, var(--color-surface));
    --color-info-surface: color-mix(in srgb, var(--color-info) 11%, var(--color-surface));
}
.palette-scope[data-usage~="surfaces"] {
    --color-surface-shaded: var(--color-accent-surface);
    --color-surface-faint: color-mix(in srgb, var(--color-accent-surface) 45%, var(--color-surface));
    --color-border-shaded: color-mix(in srgb, var(--color-accent) 14%, transparent);
}
.palette-scope[data-language="workshop"] { --radius-card: 1.5rem; }
.palette-scope[data-language="atelier"] { --radius-card: 1.25rem; }
.palette-scope[data-language="editorial"] { --radius-card: 0.5rem; }
.palette-scope[data-language="workbench"] { --radius-card: 0.875rem; }
.palette-scope[data-language="office"] { --radius-card: 0.625rem; }
` +
    inScope(
        '.palette-scope[data-usage~="orientation"]',
        `
[role="tab"] .bg-tab-line {
    background-color: var(--color-tab-line, var(--color-accent));
}
[role="img"] > span.bg-bar-current {
    background-color: var(--color-bar-current, var(--color-accent));
}
.bg-today.text-today-text {
    background-color: var(--color-today, var(--color-accent));
    color: var(--color-today-text, var(--color-on-primary));
}
header .mb-5.text-eyebrow {
    color: var(--color-eyebrow, var(--color-accent-text));
}
a.underline,
.sample-link {
    color: var(--color-link, var(--color-accent-text));
    text-decoration-color: color-mix(in srgb, var(--color-link, var(--color-accent-text)) 35%, transparent);
}`
    ) +
    inScope(
        '.palette-scope[data-usage~="secondary"]',
        `
h1 {
    width: fit-content;
    background-image: linear-gradient(transparent 58%, var(--color-secondary-surface) 58%, var(--color-secondary-surface) 92%, transparent 92%);
}`
    ) +
    inScope(
        '.palette-scope[data-language="paper"]',
        `
h1, h2, .text-3xl {
    font-family: ui-serif, 'New York', 'Iowan Old Style', Georgia, serif;
    font-weight: 500;
    letter-spacing: -0.01em;
}`
    ) +
    inScope(
        '.palette-scope[data-language="workshop"]',
        `
.lg\\:grid-cols-4 > :nth-child(1) { --color-surface-shaded: oklch(0.95 0.035 250); }
.lg\\:grid-cols-4 > :nth-child(2) { --color-surface-shaded: oklch(0.95 0.035 190); }
.lg\\:grid-cols-4 > :nth-child(3) { --color-surface-shaded: oklch(0.95 0.035 300); }
.lg\\:grid-cols-4 > :nth-child(4) { --color-surface-shaded: oklch(0.95 0.035 60); }`
    ) +
    inScope(
        '.palette-scope.dark[data-language="workshop"]',
        `
.lg\\:grid-cols-4 > :nth-child(1) { --color-surface-shaded: oklch(0.26 0.03 250); }
.lg\\:grid-cols-4 > :nth-child(2) { --color-surface-shaded: oklch(0.26 0.03 190); }
.lg\\:grid-cols-4 > :nth-child(3) { --color-surface-shaded: oklch(0.26 0.03 300); }
.lg\\:grid-cols-4 > :nth-child(4) { --color-surface-shaded: oklch(0.26 0.03 60); }`
    ) +
    inScope(
        '.palette-scope[data-headline]',
        `
h1, h2, .text-3xl {
    font-family: var(--headline-font);
    font-weight: var(--headline-weight);
    letter-spacing: var(--headline-tracking);
}`
    ) +
    inScope(
        '.palette-scope[data-language="editorial"]',
        `
h1.text-4xl { font-size: 3rem; }
@media (min-width: 48rem) {
    h1.md\\:text-5xl { font-size: 4rem; line-height: 1.05; }
}
h2 { font-size: 1.625rem; }
.text-3xl:not(h1) { font-size: 2.5rem; }`
    ) +
    inScope(
        ':is(.palette-scope[data-language="editorial"], .palette-scope[data-language="office"])',
        `
header .mb-5.text-eyebrow {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}`
    ) +
    inScope(
        '.palette-scope[data-language="workbench"]',
        `
button.rounded-full,
a.rounded-full {
    border-radius: 0.625rem;
}`
    ) +
    inScope(
        '.palette-scope[data-language="office"]',
        `
.text-3xl:not(h1) {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-weight: 500;
    letter-spacing: -0.02em;
}`
    ) +
    inScope(
        '.palette-scope[data-language="letter"]',
        `
h1 {
    font-style: italic;
    color: var(--color-accent-text);
}`
    ) +
    inScope(
        '.palette-scope[data-usage~="dark-rail"]',
        `
nav.bg-rail,
.mini-rail {
    --color-surface: #211c17;
    --color-surface-muted: #2d2620;
    --color-surface-hover: #372f28;
    --color-border: #2d2620;
    --color-text: #f3ede4;
    --color-text-muted: #a89f91;
    --color-text-subtle: #6d655a;
    --color-accent-surface: color-mix(in srgb, #e8946f 22%, #211c17);
    --color-accent-text: #f0ab8b;
    --color-nav-active: var(--color-accent-surface);
    --color-nav-active-text: var(--color-accent-text);
    background-color: var(--color-surface);
    color: var(--color-text);
}`
    ) +
    inScope('.palette-scope', `.chrome-dark {${CHROME_DARK}}`) +
    inScope('.palette-scope[data-usage~="rail-dark"]', `nav.bg-rail {${CHROME_DARK}}`) +
    inScope('.palette-scope[data-usage~="top-dark"]', `header.border-b.h-14 {${CHROME_DARK}}`) +
    inScope(
        '.palette-scope.dark[data-usage~="dark-rail"]',
        `
nav.bg-rail,
.mini-rail {
    --color-surface: #121110;
    --color-border: #262420;
}`
    );

// The same rules reach the real app through <html>; these are for the real rail and top bar, which the frames draw
// themselves.
const APP_CSS = `
html.palette-scope[data-usage~="chrome"] nav.bg-rail,
html.palette-scope[data-usage~="rail-chrome"] nav.bg-rail {
    background-color: var(--color-chrome);
}
html.palette-scope[data-usage~="chrome"] header.border-b.h-14 {
    background-color: color-mix(in srgb, var(--color-chrome) var(--top-bar-opacity), transparent);
}
html.palette-scope[data-usage~="orientation"] nav [aria-current="page"] {
    background-color: var(--color-nav-active, var(--color-accent-surface));
    color: var(--color-nav-active-text, var(--color-accent-text));
}
html.palette-scope[data-usage~="quiet-nav"] nav [aria-current="page"] {
    background-color: var(--color-surface-muted);
}
html.palette-scope[data-usage~="rail-surface"] nav.bg-rail {
    background-color: var(--color-rail);
}
html.palette-scope[data-usage~="top-surface"] header.border-b.h-14 {
    background-color: color-mix(in srgb, var(--color-top) var(--top-bar-opacity), transparent);
}
html.palette-scope[data-usage~="inset"] div.h-dvh {
    background-color: var(--color-rail);
}
html.palette-scope[data-usage~="inset"] nav.bg-rail {
    border-color: transparent;
}
@media (min-width: 48rem) {
    html.palette-scope[data-usage~="inset"] div.h-dvh > div:last-child {
        margin: 0.5rem 0.5rem 0.5rem 0;
        border-radius: 0.875rem;
        overflow: hidden;
        background-color: var(--color-surface);
        box-shadow: 0 0 0 1px var(--color-border);
    }
}
html.palette-scope[data-usage~="rail"] nav.bg-rail {
    background-color: var(--color-primary);
    color: var(--color-on-primary);
}
`;

let themeObserver: MutationObserver | null = null;

// The tokens of the theme the app shows, again whenever it switches between light and dark.
const setTokens = (preview: Preview) => {
    const root = document.documentElement;
    for (const name of [...root.style].filter((name) => name.startsWith('--color-'))) {
        root.style.removeProperty(name);
    }
    const tokens = root.classList.contains('dark') ? preview.dark : preview.light;
    for (const [name, value] of Object.entries(tokens)) root.style.setProperty(`--color-${name}`, value);
};

const show = (preview: Preview) => {
    const root = document.documentElement;
    setTokens(preview);
    for (const [name, value] of Object.entries(headlineVariables(preview.headline))) {
        root.style.setProperty(name, value);
    }
    root.classList.add('palette-scope');
    root.dataset.usage = preview.usage.join(' ');
    if (preview.language) root.dataset.language = preview.language;
    if (preview.isWarm) root.dataset.warm = '';
    if (preview.headline) root.dataset.headline = '';
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = SCOPE_CSS + APP_CSS;
    document.head.append(style);
    loadFonts();

    let isDark = root.classList.contains('dark');
    themeObserver = new MutationObserver(() => {
        if (root.classList.contains('dark') === isDark) return;
        isDark = !isDark;
        setTokens(preview);
    });
    themeObserver.observe(root, { attributes: true, attributeFilter: ['class'] });
};

const hide = () => {
    const root = document.documentElement;
    themeObserver?.disconnect();
    themeObserver = null;
    for (const name of [...root.style].filter(
        (name) => name.startsWith('--color-') || name.startsWith('--headline-')
    )) {
        root.style.removeProperty(name);
    }
    root.classList.remove('palette-scope');
    delete root.dataset.usage;
    delete root.dataset.language;
    delete root.dataset.warm;
    delete root.dataset.headline;
    document.getElementById(STYLE_ID)?.remove();
};

export const readPreview = (): Preview | null => {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        return stored ? (JSON.parse(stored) as Preview) : null;
    } catch {
        return null;
    }
};

export const applyPreview = (preview: Preview) => {
    hide();
    show(preview);
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preview));
    } catch {
        // Without storage it stays until the next reload.
    }
};

export const clearPreview = () => {
    hide();
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Nothing was kept then.
    }
};

export const restorePreview = () => {
    const preview = readPreview();
    if (preview) show(preview);
};
